import {
  CreateVsl,
  CreateVslCustomLink,
  CreateVslLinks,
  CreateVslVersion,
  Route,
  UpdateVsl,
  UpdateVslVersion,
  VslDetail,
  VslEmailHtml,
  VslFormSubmissionItem,
  VslLinkItem,
  VslLinkViewers,
  VslListItem,
  VslStats,
  VslUploadCredentials,
  VslVersionStats,
  VslVersionStatus,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

/** How often a VSL with a version uploading or encoding checks again. */
const PENDING_REFETCH_MS = 5000

const vslQueries = {
  all: () => ["vsl"],
  list: () =>
    queryOptions({
      queryKey: [...vslQueries.all(), "list"],
      queryFn: () =>
        request<VslListItem[]>({
          url: Route.VSL__ADMIN_FIND_ALL,
          method: "GET",
        }),
    }),
  detail: (vslId: string) =>
    queryOptions({
      queryKey: [...vslQueries.all(), vslId, "detail"],
      queryFn: () =>
        request<VslDetail>({
          url: populateRoute(Route.VSL__ADMIN_FIND_ONE, vslId),
          method: "GET",
        }),
      refetchInterval: (query) =>
        query.state.data?.versions.some(
          (version) =>
            version.status === VslVersionStatus.UPLOADING ||
            version.status === VslVersionStatus.PROCESSING,
        )
          ? PENDING_REFETCH_MS
          : false,
    }),
  stats: (vslId: string) =>
    queryOptions({
      queryKey: [...vslQueries.all(), vslId, "stats"],
      queryFn: () =>
        request<VslStats>({
          url: populateRoute(Route.VSL__ADMIN_STATS, vslId),
          method: "GET",
        }),
    }),
  links: (vslId: string) =>
    queryOptions({
      queryKey: [...vslQueries.all(), vslId, "links"],
      queryFn: () =>
        request<VslLinkItem[]>({
          url: populateRoute(Route.VSL__ADMIN_LINKS, vslId),
          method: "GET",
        }),
    }),
  submissions: (vslId: string) =>
    queryOptions({
      queryKey: [...vslQueries.all(), vslId, "submissions"],
      queryFn: () =>
        request<VslFormSubmissionItem[]>({
          url: populateRoute(Route.VSL__ADMIN_SUBMISSIONS, vslId),
          method: "GET",
        }),
    }),
  versionStats: (versionId: string) =>
    queryOptions({
      queryKey: [...vslQueries.all(), "version", versionId, "stats"],
      queryFn: () =>
        request<VslVersionStats>({
          url: populateRoute(Route.VSL__ADMIN_VERSION_STATS, versionId),
          method: "GET",
        }),
    }),
  linkViewers: (linkId: string) =>
    queryOptions({
      queryKey: [...vslQueries.all(), "link", linkId, "viewers"],
      queryFn: () =>
        request<VslLinkViewers>({
          url: populateRoute(Route.VSL__ADMIN_LINK_VIEWERS, linkId),
          method: "GET",
        }),
    }),
}

export default vslQueries

/** Everything about VSLs is refetched: edits show up in stats and lists. */
const useInvalidateVsl = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: vslQueries.all() })
}

export const useCreateVsl = () => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: (data: CreateVsl) =>
      request<string>({ url: Route.VSL__ADMIN_CREATE, method: "POST", data }),
    onSuccess: invalidate,
  })
}

export const useUpdateVsl = (vslId: string) => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: (data: UpdateVsl) =>
      request<void>({
        url: populateRoute(Route.VSL__ADMIN_UPDATE, vslId),
        method: "PUT",
        data,
      }),
    onSuccess: invalidate,
  })
}

export const useCreateVslVersion = (vslId: string) =>
  useMutation({
    mutationFn: (data: CreateVslVersion) =>
      request<VslUploadCredentials>({
        url: populateRoute(Route.VSL__ADMIN_CREATE_VERSION, vslId),
        method: "POST",
        data,
      }),
  })

export const useMarkVslVersionUploaded = () => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: (versionId: string) =>
      request<void>({
        url: populateRoute(Route.VSL__ADMIN_VERSION_UPLOADED, versionId),
        method: "POST",
      }),
    onSuccess: invalidate,
  })
}

export const useUpdateVslVersion = (versionId: string) => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: (data: UpdateVslVersion) =>
      request<void>({
        url: populateRoute(Route.VSL__ADMIN_UPDATE_VERSION, versionId),
        method: "PUT",
        data,
      }),
    onSuccess: invalidate,
  })
}

export const useMakeVslVersionLive = () => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: (versionId: string) =>
      request<void>({
        url: populateRoute(Route.VSL__ADMIN_MAKE_VERSION_LIVE, versionId),
        method: "POST",
      }),
    onSuccess: invalidate,
  })
}

export const useCreateVslLinks = (vslId: string) => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: (data: CreateVslLinks) =>
      request<void>({
        url: populateRoute(Route.VSL__ADMIN_CREATE_LINKS, vslId),
        method: "POST",
        data,
      }),
    onSuccess: invalidate,
  })
}

export const useCreateVslCustomLink = (vslId: string) => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: (data: CreateVslCustomLink) =>
      request<void>({
        url: populateRoute(Route.VSL__ADMIN_CREATE_CUSTOM_LINK, vslId),
        method: "POST",
        data,
      }),
    onSuccess: invalidate,
  })
}

export const useUploadVslVersionThumbnail = (versionId: string) => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: (image: File) => {
      const formData = new FormData()
      formData.append("image", image)
      return request<void>({
        url: populateRoute(
          Route.VSL__ADMIN_UPLOAD_VERSION_THUMBNAIL,
          versionId,
        ),
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      })
    },
    onSuccess: invalidate,
  })
}

export const useRemoveVslVersionThumbnail = (versionId: string) => {
  const invalidate = useInvalidateVsl()
  return useMutation({
    mutationFn: () =>
      request<void>({
        url: populateRoute(
          Route.VSL__ADMIN_REMOVE_VERSION_THUMBNAIL,
          versionId,
        ),
        method: "DELETE",
      }),
    onSuccess: invalidate,
  })
}

export const fetchVslEmailHtml = (vslId: string, linkId: string | null) =>
  request<VslEmailHtml>({
    url: populateRoute(Route.VSL__ADMIN_EMAIL_HTML, vslId),
    method: "GET",
    params: linkId ? { linkId } : {},
  })
